import React, { useState, useEffect } from 'react';
import { X, Building2, Plus, MapPin, Search, AlertCircle } from 'lucide-react';
import GoogleMapPicker from '../GoogleMapPicker';
import SetSupplierLocationModal from './SetSupplierLocationModal';
import { safeFetch, extractError } from '../../lib/safeFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AddSupplierToVisit({ visit, onClose }) {
  const [suppliers, setSuppliers] = useState([]);
  const [pickerValue, setPickerValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState('search'); // 'search' | 'create'
  const [locationTarget, setLocationTarget] = useState(null);

  const existing = visit.supplier_ids || (visit.supplier_id ? [visit.supplier_id] : []);

  useEffect(() => { fetchSuppliers(); /* eslint-disable-line */ }, []);

  const fetchSuppliers = async () => {
    const token = localStorage.getItem('token');
    const r = await fetch(`${API_URL}/api/suppliers`, { headers: { 'Authorization': `Bearer ${token}` } });
    const data = await r.json();
    setSuppliers((data || []).filter(s => !existing.includes(s.id)));
  };

  const onLocationSaved = (updated) => {
    setSuppliers(prev => prev.map(s => s.id === updated.id
      ? { ...s, location: updated.location, has_location: true }
      : s
    ));
    // The attach was intercepted because location was missing — resume now.
    setLocationTarget(null);
    attachSupplierDirect(updated.id);
  };

  // Internal attach that bypasses the has_location check (used after the
  // location-picker flow completes).
  const attachSupplierDirect = async (supplierId) => {
    if (loading) return;
    setLoading(true);
    const token = localStorage.getItem('token');
    const result = await safeFetch(`${API_URL}/api/visits/${visit.id}/add-supplier`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ supplier_id: supplierId }),
    });
    if (result.ok) {
      alert(result.data?.message || 'Supplier added');
      onClose();
    } else {
      alert(extractError(result, 'Failed to add supplier'));
    }
    setLoading(false);
  };

  const filtered = suppliers.filter(s => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (s.name || '').toLowerCase().includes(q) || (s.address || '').toLowerCase().includes(q);
  });

  const attachSupplier = async (supplierId) => {
    if (loading) return;
    const supplier = suppliers.find(s => s.id === supplierId);
    if (supplier && !supplier.has_location) {
      // Force the location-picker to run before attach.
      setLocationTarget(supplier);
      return;
    }
    setLoading(true);
    const token = localStorage.getItem('token');
    const result = await safeFetch(`${API_URL}/api/visits/${visit.id}/add-supplier`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ supplier_id: supplierId }),
    });
    if (result.ok) {
      alert(result.data?.message || 'Supplier added');
      onClose();
    } else {
      alert(extractError(result, 'Failed to add supplier'));
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-end sm:items-center justify-center" data-testid="add-supplier-modal">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-bold flex items-center"><Building2 size={20} className="mr-2 text-blue-600" />Add Supplier</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
        </div>

        <p className="text-xs text-gray-600 mb-3">
          Currently visiting <strong>{visit.supplier_name}</strong>. Attach an existing supplier or create a new one.
        </p>

        {/* Mode switch */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg mb-3" data-testid="add-supplier-mode">
          <button
            type="button"
            onClick={() => setMode('search')}
            className={`flex-1 py-1.5 text-xs font-semibold rounded-md flex items-center justify-center gap-1 ${mode === 'search' ? 'bg-white shadow text-blue-600' : 'text-gray-600'}`}
            data-testid="mode-search"
          ><Search size={12} />Existing</button>
          <button
            type="button"
            onClick={() => setMode('create')}
            className={`flex-1 py-1.5 text-xs font-semibold rounded-md flex items-center justify-center gap-1 ${mode === 'create' ? 'bg-white shadow text-blue-600' : 'text-gray-600'}`}
            data-testid="mode-create"
          ><Plus size={12} />New Supplier</button>
        </div>

        {mode === 'search' ? (
          <SearchMode
            search={search} setSearch={setSearch}
            filtered={filtered}
            pickerValue={pickerValue} setPickerValue={setPickerValue}
            attach={attachSupplier} loading={loading}
          />
        ) : (
          <CreateMode onCreated={(sid) => attachSupplierDirect(sid)} loading={loading} setLoading={setLoading} />
        )}
      </div>

      {locationTarget && (
        <SetSupplierLocationModal
          supplier={locationTarget}
          onClose={() => setLocationTarget(null)}
          onSaved={onLocationSaved}
        />
      )}
    </div>
  );
}


function SearchMode({ search, setSearch, filtered, pickerValue, setPickerValue, attach, loading }) {
  return (
    <>
      <input
        type="text"
        placeholder="Search supplier by name or address…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3"
        data-testid="add-supplier-search"
      />

      <div className="space-y-1.5 max-h-72 overflow-y-auto mb-4 border rounded-lg" data-testid="add-supplier-list">
        {filtered.length === 0 && <p className="p-4 text-center text-sm text-gray-400">No matching suppliers.</p>}
        {filtered.map(s => {
          const hasLoc = !!s.has_location;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setPickerValue(s.id)}
              className={`w-full text-left p-3 border-b last:border-b-0 hover:bg-gray-50 ${pickerValue === s.id ? 'bg-blue-50 border-blue-200' : ''}`}
              data-testid={`add-supplier-option-${s.id}`}
            >
              <div className="flex items-center gap-1.5">
                <span
                  className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                    hasLoc ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                  }`}
                  data-testid={`add-supplier-gps-${s.id}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${hasLoc ? 'bg-emerald-500' : 'bg-red-500'}`} />
                  GPS
                </span>
                <p className="text-sm font-medium flex-1 truncate">{s.name}</p>
              </div>
              {s.address && <p className="text-[11px] text-gray-500 truncate mt-0.5">{s.address}</p>}
              {!hasLoc && (
                <p className="text-[11px] text-red-600 flex items-center gap-1 mt-0.5">
                  <AlertCircle size={10} />
                  Tap "Add to Trip" to pin location first.
                </p>
              )}
              {s.created_by_name && (
                <p className="text-[10px] text-gray-400 mt-0.5">by {s.created_by_name}</p>
              )}
            </button>
          );
        })}
      </div>

      <button
        onClick={() => attach(pickerValue)}
        disabled={!pickerValue || loading}
        className="w-full flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50"
        data-testid="add-supplier-confirm-btn"
      >
        <Plus size={18} />
        <span>{loading ? 'Adding…' : 'Add to Trip'}</span>
      </button>
    </>
  );
}


function CreateMode({ onCreated, loading, setLoading }) {
  const [form, setForm] = useState({ name: '', address: '', latitude: null, longitude: null });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.address.trim() || form.latitude == null || form.longitude == null) {
      alert('Please enter name, address, and pick a location on the map.');
      return;
    }
    if (busy) return;
    setBusy(true);
    const token = localStorage.getItem('token');
    const result = await safeFetch(`${API_URL}/api/suppliers`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: form.name.trim(),
        address: form.address.trim(),
        latitude: form.latitude,
        longitude: form.longitude,
      }),
    });
    if (!result.ok) {
      alert(extractError(result, 'Create failed'));
      setBusy(false);
      return;
    }
    onCreated(result.data?.supplier_id);
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="add-supplier-create-form">
      <input
        type="text"
        placeholder="Supplier name"
        value={form.name}
        onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
        className="w-full px-3 py-2 border rounded-lg text-sm"
        data-testid="new-supplier-name"
        required
      />
      <textarea
        placeholder="Address"
        value={form.address}
        onChange={(e) => setForm(p => ({ ...p, address: e.target.value }))}
        className="w-full px-3 py-2 border rounded-lg text-sm"
        data-testid="new-supplier-address"
        rows={2}
        required
      />
      <div>
        <label className="block text-xs text-gray-600 mb-1">Pin location (satellite ↔ map toggle top-right)</label>
        <GoogleMapPicker
          value={form.latitude && form.longitude ? { latitude: form.latitude, longitude: form.longitude } : null}
          onChange={(c) => setForm(p => ({ ...p, latitude: c.latitude, longitude: c.longitude }))}
          height={240}
        />
        <p className="mt-1 text-[11px] text-gray-500 font-mono">
          {form.latitude ? form.latitude.toFixed(6) : '—'}, {form.longitude ? form.longitude.toFixed(6) : '—'}
        </p>
      </div>
      <button
        type="submit"
        disabled={busy || loading}
        className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2"
        data-testid="new-supplier-submit"
      >
        <Plus size={16} />
        {busy || loading ? 'Saving…' : 'Create & Add to Trip'}
      </button>
    </form>
  );
}
